import React, { useState } from 'react';
import axios, { API, SUPPORT_EMAIL } from '../lib/api';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { MessageSquare, Send, Bug, Lightbulb, MoreHorizontal } from 'lucide-react';
import { toast } from 'sonner';

const FEEDBACK_CATEGORIES = [
  { value: 'bug', label: 'Bug / Issue', icon: Bug, color: 'text-red-600' },
  { value: 'suggestion', label: 'Suggestion', icon: Lightbulb, color: 'text-amber-600' },
  { value: 'other', label: 'Other', icon: MoreHorizontal, color: 'text-gray-600' },
];

export const getCategoryMeta = (value) => {
  return FEEDBACK_CATEGORIES.find((c) => c.value === value) || FEEDBACK_CATEGORIES[2];
};

const FeedbackButton = () => {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('bug');
  const [message, setMessage] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = message.trim();
    const trimmedEmail = contactEmail.trim();
    if (!trimmed) { toast.error('Please enter a message'); return; }
    if (trimmed.length > 2000) { toast.error('Message is too long (max 2000 characters)'); return; }
    if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      toast.error('Please enter a valid email or leave it blank');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/feedback`, { category, message: trimmed, contact_email: trimmedEmail || null });
      toast.success('Thanks for the feedback!');
      setMessage('');
      setContactEmail('');
      setCategory('bug');
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const getPlaceholder = () => {
    if (category === 'bug') return 'Describe what went wrong, what you expected, and steps to reproduce...';
    if (category === 'suggestion') return 'What would make Just B better for you?';
    return "Tell us what's on your mind...";
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          aria-label="Send feedback"
          className="fixed bottom-6 right-6 z-50 rounded-full h-14 w-14 shadow-lg bg-justb-primary hover:bg-justb-primary/90 p-0 flex items-center justify-center"
        >
          <MessageSquare className="h-6 w-6" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-justb-primary" />
            Send feedback
          </DialogTitle>
          <p className="text-sm text-gray-500 pt-1">Help us improve Just B. Your feedback goes straight to the team.</p>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="mb-2 block">Category</Label>
            <div className="grid grid-cols-3 gap-2">
              {FEEDBACK_CATEGORIES.map((cat) => {
                const Icon = cat.icon;
                const selected = category === cat.value;
                return (
                  <button
                    type="button"
                    key={cat.value}
                    onClick={() => setCategory(cat.value)}
                    className={`flex flex-col items-center gap-1 p-3 rounded-lg border-2 transition-all ${
                      selected ? 'border-justb-primary bg-justb-primary/5' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Icon className={`h-5 w-5 ${cat.color}`} />
                    <span className="text-xs font-medium text-gray-700">{cat.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <Label htmlFor="feedback-message" className="mb-2 block">Your message</Label>
            <Textarea
              id="feedback-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={getPlaceholder()}
              rows={5}
              maxLength={2000}
              required
            />
            <p className="text-xs text-gray-400 mt-1 text-right">{message.length}/2000</p>
          </div>
          <div>
            <Label htmlFor="feedback-email" className="mb-2 block">
              Email <span className="text-gray-400 font-normal">(optional, for follow-up)</span>
            </Label>
            <Input
              id="feedback-email"
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
            />
            <p className="text-xs text-gray-500 mt-1">We'll only use this to reply to you about your feedback.</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={submitting}>Cancel</Button>
            <Button type="submit" disabled={submitting || !message.trim()} className="bg-justb-primary hover:bg-justb-primary/90">
              <Send className="h-4 w-4 mr-2" />
              {submitting ? 'Sending...' : 'Send feedback'}
            </Button>
          </div>
          <p className="text-xs text-gray-400 text-center pt-1">
            Prefer email? Reach us at{' '}
            <a href={`mailto:${SUPPORT_EMAIL}?subject=Just%20B%20-%20Feedback`} className="text-justb-secondary hover:underline">
              {SUPPORT_EMAIL}
            </a>
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackButton;
